import { useRef, useState } from "react";
import { useAvailableAttributeGroups } from "./useAvailableAttributeGroups";
// Redux
import { useDispatch, useSelector } from "react-redux";
// Components
import { translateMessage } from "@delibrandev/utils";
import {
  getProduct,
  updateAttributesGroup, // /admin/product/{productId}/add-item-groups
  adminRemoveItemGroupFromProduct, // /admin/product/{productId}/remove-item-group/{itemGroupId}
  adminRemoveItemFromProductGroup, // /admin/product/{productId}/remove/{itemGroupId}/item/{itemId}
  adminAddCustomItemGroupToProduct, // /admin/product/{productId}/add-item-group/{itemGroupId}
} from "../../../../../api/products";
import { setAlert, setLoading, updateFloatPanelData } from "../../../../../reducers/userConfigSlice";

export const Attributes = ({ compact = false }) => {
  const [availableOpen, setAvailableOpen] = useState(!compact);
  const dispatch = useDispatch();
  const inFlight = useRef(false);
  const [saving, setSaving] = useState(false);
  const idProduct = useSelector((state) => state.aplicationConfig.userConfig.floatPanel.queryId);
  const {groups:allGroupAttributes,loading:groupsLoading,error:groupsError,retry:retryGroups} = useAvailableAttributeGroups(idProduct);
  const groupAttributes = useSelector((state) => state.aplicationConfig.userConfig.floatPanel.data.itemGroups);

  const toast = (message) => dispatch(setAlert(translateMessage(message || "Bad request")));

  const selectedGroupIds = (groupAttributes || []).map(group => group.masterItemGroupId || group.itemGroupId);

  const mutate = async request => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    dispatch(setLoading(true));
    try {
      const response = await request();
      if (!response || response.error) {
        toast(response?.message || "No se pudieron guardar los atributos. Vuelve a intentarlo.");
        return;
      }
      try {
        const refreshed = await getProduct(idProduct);
        if (!refreshed || refreshed.error) throw new Error("Refresh failed");
        dispatch(updateFloatPanelData({ data: refreshed.data.itemGroups, name: "itemGroups" }));
        toast(response.message);
      } catch {
        toast("Los cambios se han guardado, pero no se pudieron actualizar los atributos. Cierra y vuelve a abrir el producto.");
      }
    } catch {
      toast("No se pudieron guardar los atributos. Vuelve a intentarlo.");
    } finally {
      inFlight.current = false;
      setSaving(false);
      dispatch(setLoading(false));
    }
  };

  const removeActiveGroup = group => mutate(() => adminRemoveItemGroupFromProduct(idProduct, group.itemGroupId));
  const removeActiveItem = (group, item) => mutate(() => adminRemoveItemFromProductGroup(idProduct, group.itemGroupId, item.itemId));
  const addFullGroupIfNotActive = group => {
    if (selectedGroupIds.includes(group.itemGroupId)) return;
    return mutate(() => updateAttributesGroup(idProduct, group.itemGroupId, {
      itemGroups: [{ itemGroupId: group.itemGroupId, itemIds: (group.items || []).map(item => item.itemId).filter(Boolean) }],
    }));
  };
  const addCustomGroupWithItem = (group, item) => mutate(() => adminAddCustomItemGroupToProduct(idProduct, group.itemGroupId, { items: [item?.name].filter(Boolean) }));

  // ------- INTERNAL COMPONENTS (same file) -------

  const Clickable = ({ onClick, ariaPressed, className, children }) => {
    const handleKeyDown = (e) => {
      if (inFlight.current) return;
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar" || e.key === "Space") {
        e.preventDefault();
        onClick?.(e);
      }
    };

    return (
      <div
        role="button"
        tabIndex={saving ? -1 : 0}
        aria-disabled={saving}
        aria-pressed={ariaPressed}
        className={className}
        onClick={event => { if (!inFlight.current) onClick?.(event); }}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    );
  };

  const GroupTitle = ({ name }) => {
    const title = name?.charAt(0).toUpperCase() + name?.slice(1);
    return (
      <div className="attributes-organitation-area-title">
        <b>{title}</b>
      </div>
    );
  };

  const GroupItems = ({ group, mode }) => {
    // mode: "active" | "available"
    return (
      <div className="attributes-organitation-area-items">
        {(group.items || []).map((opt, indexItem) => {
          const key = "attributes-organitation-area-item-" + group.itemGroupId + "-" + opt.itemId + "-" + indexItem;

          const handleItemClick = (e) => {
            // Muy importante: evitar que el click del item dispare también el click del grupo
            e?.stopPropagation?.();
            if (inFlight.current) return;

            if (mode === "active") {
              removeActiveItem(group, opt);
            } else {
              addCustomGroupWithItem(group, opt);
            }
          };

          const handleItemKeyDown = (e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "Spacebar" || e.key === "Space") {
              e.preventDefault();
              e?.stopPropagation?.();
              handleItemClick(e);
            }
          };

          return (
            <div
              key={key}
              role="button"
              tabIndex={saving ? -1 : 0}
              aria-disabled={saving}
              aria-pressed={true}
              className="attributes-organitation-area-item attributes-organitation-area-item-active"
              onClick={handleItemClick}
              onKeyDown={handleItemKeyDown}
            >
              {opt.name?.charAt(0).toUpperCase() + opt.name?.slice(1)}
            </div>
          );
        })}
      </div>
    );
  };

  const AttributeGroupCard = ({ group, index, mode }) => {
    if (!group?.items || group.items.length === 0) return null;

    const containerClass =
      mode === "active" ? "attributes-organitation-area attributes-organitation-area-selected" : "attributes-organitation-area";

    const onGroupClick = () => {
      if (mode === "active") {
        removeActiveGroup(group);
      } else {
        addFullGroupIfNotActive(group);
      }
    };

    return (
      <Clickable
        key={"attributes-organitation-area-" + index}
        ariaPressed={mode === "active" ? true : false}
        className={containerClass}
        onClick={onGroupClick}
      >
        <GroupTitle name={group.name} />
        <GroupItems group={group} mode={mode} />
      </Clickable>
    );
  };

  // ------- RENDER -------

  return (
    <>
      {saving && <p role="status">Guardando atributos…</p>}
      <div>
        <div className="inputDescription mt-2">Atributos activos</div>
      </div>

      <div className="groups-products">
        {groupAttributes &&
          groupAttributes.length > 0 &&
          groupAttributes.map((group, index) => (
            <AttributeGroupCard key={"active-group-" + index} group={group} index={index} mode="active" />
          ))}
      </div>

      {compact && <><p className="productAttributeEmpty">{groupAttributes?.length ? `${groupAttributes.length} agrupaciones incluidas en este producto.` : "Este producto no tiene atributos."}</p><button type="button" className="customButton" aria-expanded={availableOpen} disabled={saving} onClick={() => setAvailableOpen(value => !value)}>{availableOpen ? "Cerrar selección" : "+ Añadir agrupación"}</button></>}
      <section hidden={!availableOpen} className="productAttributePicker">
      {!compact && <hr />}
      <div>
        <div className="inputDescription">Atributos disponibles</div>
        {groupsLoading && <p role="status">Cargando atributos…</p>}
        {groupsError && <div role="alert"><p>{groupsError}</p><button type="button" onClick={retryGroups}>Reintentar</button></div>}
        {!groupsLoading && !groupsError && !allGroupAttributes.some(group => group.items?.length) && <p>No hay atributos disponibles. Crea un grupo con atributos para añadirlos al producto.</p>}
      </div>

      <div className="groups-products">
        {allGroupAttributes &&
          allGroupAttributes.length > 0 &&
          allGroupAttributes.map((group, index) => {
            if (!group?.items || group.items.length === 0) return null;

            // Aquí SI mostramos aunque esté activo (son "todos los que existen"),
            // pero el click de grupo no hará nada si ya está activo (criterio tuyo).
            return <AttributeGroupCard key={"available-group-" + index} group={group} index={index} mode="available" />;
          })}
      </div>
      </section>
    </>
  );
};
